import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import NatureIcon from '@mui/icons-material/Nature';
import MenuIcon from '@mui/icons-material/Menu';
import SwitchAccountOutlinedIcon from '@mui/icons-material/SwitchAccountOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { useAuth } from '@/contexts/AuthContext';
import { ROLE_LABELS } from '@/lib/permissions';
import { UserRole } from '@/types/enums';
import NotificationBadge from '@/components/shared/NotificationBadge';
import NotificationCenter from '@/components/shared/NotificationCenter';

// ── Warna aksen per role ──────────────────────────────────────────────────────
export const ROLE_COLORS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]:       '#5c35d9',   // ungu – platform
  [UserRole.ADMIN_BANK_SAMPAH]: '#1976d2',   // biru – tenant owner
  [UserRole.PENGELOLA]:         '#2e7d32',   // hijau – operasional
  [UserRole.NASABAH]:           '#e65100',   // oranye – end user
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  zIndex: theme.zIndex.drawer + 1,
}));

const UserAvatarButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'default',
  userSelect: 'none',
}));

// ── Component ─────────────────────────────────────────────────────────────────

interface DashboardHeaderProps {
  /** Bila diisi, tampilkan tombol burger untuk membuka sidebar navigasi. */
  onMenuClick?: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ onMenuClick }) => {
  const {
    user,
    logout,
    effectiveRole,
    isNasabahMode,
    canEnterNasabahMode,
    enterNasabahMode,
    exitNasabahMode,
  } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut]   = useState(false);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);

  if (!user) return null;

  // Chip role mengikuti tampilan aktif: saat Mode Nasabah, tampilkan "Nasabah"
  // agar staf sadar sedang melihat aplikasi sebagai nasabah.
  const displayRole = effectiveRole ?? user.role;
  const roleColor  = ROLE_COLORS[displayRole] ?? '#1976d2';
  const roleLabel  = ROLE_LABELS[displayRole] ?? displayRole;
  const originalRoleLabel = ROLE_LABELS[user.role] ?? user.role;
  const initials   = user.fullName
    ? user.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : user.email.slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    // AuthContext akan mengarahkan ulang via PublicOnlyRoute / ProtectedRoute
    setLoggingOut(false);
    setConfirmOpen(false);
  };

  return (
    <>
      <StyledAppBar position="fixed" elevation={0}>
        <Toolbar sx={{ minHeight: 56, px: { xs: 2, sm: 3 } }}>
          {/* Burger — buka sidebar navigasi */}
          {onMenuClick && (
            <IconButton
              edge="start"
              onClick={onMenuClick}
              sx={{ mr: 1, color: 'text.primary' }}
              aria-label="Buka menu navigasi"
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 2 }}>
            <NatureIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: { xs: 'none', sm: 'block' },
                lineHeight: 1,
              }}
            >
              PeduliSampah
            </Typography>
          </Stack>

          <Box flex={1} />

          {/* Tombol masuk Mode Nasabah — hanya untuk staf, saat di mode asli */}
          {canEnterNasabahMode && !isNasabahMode && (
            <Tooltip title="Masuk Mode Nasabah" placement="bottom">
              <IconButton
                size="small"
                onClick={enterNasabahMode}
                sx={{
                  mr: 0.5,
                  color: ROLE_COLORS[UserRole.NASABAH],
                  '&:hover': { bgcolor: alpha(ROLE_COLORS[UserRole.NASABAH], 0.08) },
                }}
                aria-label="Masuk Mode Nasabah"
              >
                <SwitchAccountOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Notification Badge */}
          <NotificationBadge onClick={() => setNotificationCenterOpen(true)} />

          {/* User info */}
          <UserAvatarButton>
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: '0.75rem',
                fontWeight: 700,
                bgcolor: alpha(roleColor, 0.15),
                color: roleColor,
                border: `1.5px solid ${alpha(roleColor, 0.4)}`,
              }}
            >
              {initials}
            </Avatar>

            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography
                variant="caption"
                display="block"
                fontWeight={600}
                color="text.primary"
                lineHeight={1.2}
              >
                {user.fullName || user.email}
              </Typography>
              <Chip
                label={roleLabel}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: alpha(roleColor, 0.12),
                  color: roleColor,
                  border: `1px solid ${alpha(roleColor, 0.3)}`,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Box>
          </UserAvatarButton>

          {/* Logout button */}
          <Tooltip title="Keluar dari akun" placement="bottom-end">
            <IconButton
              size="small"
              onClick={() => setConfirmOpen(true)}
              sx={{
                ml: 1.5,
                color: 'text.secondary',
                '&:hover': { color: 'error.main', bgcolor: alpha('#f44336', 0.08) },
              }}
              aria-label="Keluar"
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </StyledAppBar>

      {/* Spacer agar konten tidak tertutup AppBar */}
      <Box sx={{ height: 56 }} />

      {/* Banner Mode Nasabah — selalu tampak selama mode aktif, dengan
          tombol kembali ke mode asli (Pengelola/Admin). */}
      {isNasabahMode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 0.75,
            bgcolor: alpha(ROLE_COLORS[UserRole.NASABAH], 0.1),
            borderBottom: `1px solid ${alpha(ROLE_COLORS[UserRole.NASABAH], 0.3)}`,
          }}
        >
          <Typography variant="caption" fontWeight={600} sx={{ color: ROLE_COLORS[UserRole.NASABAH] }}>
            Anda sedang dalam Mode Nasabah
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<KeyboardReturnIcon />}
            onClick={exitNasabahMode}
            sx={{
              py: 0,
              fontSize: '0.7rem',
              textTransform: 'none',
              color: ROLE_COLORS[user.role],
              borderColor: alpha(ROLE_COLORS[user.role], 0.5),
              '&:hover': {
                borderColor: ROLE_COLORS[user.role],
                bgcolor: alpha(ROLE_COLORS[user.role], 0.08),
              },
            }}
          >
            Kembali ke Mode {originalRoleLabel}
          </Button>
        </Box>
      )}

      {/* Konfirmasi logout */}
      <Dialog
        open={confirmOpen}
        onClose={() => !loggingOut && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Keluar dari Akun?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sesi Anda akan diakhiri. Anda perlu login kembali untuk mengakses aplikasi.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={loggingOut}
            variant="outlined"
          >
            Batal
          </Button>
          <Button
            onClick={handleLogout}
            disabled={loggingOut}
            variant="contained"
            color="error"
            startIcon={<LogoutIcon />}
          >
            {loggingOut ? 'Keluar…' : 'Ya, Keluar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Center */}
      <NotificationCenter
        open={notificationCenterOpen}
        onClose={() => setNotificationCenterOpen(false)}
      />
    </>
  );
};

export default DashboardHeader;
